import { computed, makeObservable, observable, runInAction } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { getUserHasSlideDeckReviewDocument } from '../firestore/UserProfileSlideDeckReview'
import { SlideDeck } from '../models/SlideDeck'
import { fetchSlideDeck } from '../firestore/SlideDeck'

// We often land here after the invitation token page, so we should wait a bit for the review doc to
// be written in case the invitation handler is still processing
const TIME_TO_WAIT_FOR_REVIEW_DOC_MS = 5000

export class SlideDeckReviewCubit extends Cubit {
  repository: FirebaseRepository
  private _isInternalUser: boolean
  private _slideDeckId: string
  slideDeck: SlideDeck

  @observable
  private _timeToWaitForReviewDocPassed = false

  canReviewSlideDeck = observable.box<boolean | null>(null) // null unknown

  constructor(
    repository: FirebaseRepository,
    {
      isInternalUser,
      slideDeckId,
    }: { isInternalUser: boolean; slideDeckId: string }
  ) {
    super()
    this.repository = repository
    this._isInternalUser = isInternalUser
    this._slideDeckId = slideDeckId
    this.slideDeck = SlideDeck.empty(repository)

    makeObservable(this)
  }

  initialize(): void {
    this.addReaction({
      whenThisChanges: () => this.canReviewSlideDeck.get(),
      thenRunThisCode: async (canReview) => {
        // don't fetch slide deck if id is empty
        // this can happen temporarily during navigation
        if (!canReview || !this._slideDeckId) return
        const slideDeckDoc = await fetchSlideDeck(this.repository, {
          slideDeckId: this._slideDeckId,
        })
        this.slideDeck.replaceModel(slideDeckDoc)
      },
      fireImmediately: true, // in case user internal
    })

    if (this._isInternalUser)
      return runInAction(() => this.canReviewSlideDeck.set(true))

    if (this._slideDeckId) {
      this.addStream(
        getUserHasSlideDeckReviewDocument(this.repository, {
          userId: this.repository.auth.currentUser?.uid ?? '',
          slideDeckId: this._slideDeckId,
        }),
        (hasReviewDoc) => {
          runInAction(() => this.canReviewSlideDeck.set(hasReviewDoc))
        }
      )
    }

    const timeout = setTimeout(() => {
      runInAction(() => (this._timeToWaitForReviewDocPassed = true))
    }, TIME_TO_WAIT_FOR_REVIEW_DOC_MS)

    this.disposers.push(() => clearTimeout(timeout))
  }

  @computed
  get canUserReviewSlideDeck():
    | { loading: true }
    | { loading: false; canView: true }
    | { loading: false; canView: false; reason: 'unauthorized' | 'internal' } {
    const userAuthorized = this.canReviewSlideDeck.get()
    if (!userAuthorized) {
      if (this._timeToWaitForReviewDocPassed)
        return { canView: false, reason: 'unauthorized', loading: false }
      return { loading: true }
    }
    if (this.slideDeck.isLoading) return { loading: true }

    return { canView: true, loading: false }
  }
}
